import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { MultiSelect } from "primereact/multiselect";
import CourseService from "../../../../services/CourseService";
import AlertService from "../../../../services/AlertService";
import AuthContext from "../../../../auth/AuthContext";
import ExpertSessionsService from "../../../../services/ExpertSessionsService";
import ConfirmDialogBox from "../../../confirmDialog/ConfirmDialogBox";
import { FaTrash } from "react-icons/fa";
import { useLocation } from "react-router-dom";

const CreateExpertSession = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [meetingType, setMeetingType] = useState("Teams");
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [errors, setErrors] = useState({});
  const { state } = useLocation();
  const storedId = sessionStorage.getItem("expertSessionId");
  const id = state?.id || storedId;
  const [sessionId, setSessionId] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [fromMeridian, setFromMeridian] = useState("AM");
  const [toMeridian, setToMeridian] = useState("AM");
  const [permissions, setPermissions] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitControllerRef = useRef(null);
  
  useEffect(() => {
    const expertSessionAccess = authContext.getPermissions("dashboard");
    if (expertSessionAccess.filter((f) => f.access.includes(1)).length === 0) {
      navigate("/");
    }
    setPermissions({
      showAdd: expertSessionAccess.some((f) => f.access.includes(2)),
      showEdit: expertSessionAccess.some((f) => f.access.includes(3)),
      showDelete: expertSessionAccess.some((f) => f.access.includes(4)),
    });
  }, [authContext, navigate]);

  const hours = [
    "01",
    "02",
    "03",
    "04",
    "05",
    "06",
    "07",
    "08",
    "09",
    "10",
    "11",
    "12",
  ];
  const minutes = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0"),
  );

  const [formData, setFormData] = useState({
    title: "",
    courseId: "",
    instructors: [],
    coordinators: [],
    date: "",
    fromHour: "01",
    fromMinute: "00",
    toHour: "01",
    toMinute: "00",
    description: "",
    meetingLink: "",
    meetingPlatform: "teams",
    meetingUrl: "",
    joinMeetingId: "",
    meetingId: "",
    passcode: "",
  });

  useEffect(() => {
    if (!id) {
      setIsEditing(false);
      setSessionId(0);

      setFormData({
        title: "",
        courseId: "",
        instructors: [],
        coordinators: [],
        date: "",
        fromHour: "01",
        fromMinute: "00",
        toHour: "01",
        toMinute: "00",
        description: "",
        meetingLink: "",
        meetingPlatform: "teams",
        meetingUrl: "",
        joinMeetingId: "",
        meetingId: "",
        passcode: "",
      });

      setFromMeridian("AM");
      setToMeridian("AM");
      sessionStorage.removeItem("expertSessionId");
    }
  }, [id]);

const loadCourses = useCallback(async (signal) => {
    try {
      const data = await CourseService.GetCourses(authContext, signal);

      if (signal?.aborted) return;

      if (data.isSuccess) {
        setCourses(data.data.map((c) => ({ label: c.label, value: c.value })));
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    }
  }, [authContext]);


 const loadInstructorsByCourse = useCallback(async (courseId, signal) => { 
    if (!courseId) {
      setUsers([]);
      return;
    }
    try {
      setLoadingUsers(true);
      const data = await ExpertSessionsService.GetInstructorId(
        courseId,
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data?.isSuccess) {
        setUsers(data.data.map((u) => ({ label: u.label, value: u.value })));
      } else {
        AlertService.error(data?.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error(err);
      AlertService.error("Failed to load instructors");
    } finally {
      if (!signal?.aborted) setLoadingUsers(false);
    }
  }, [authContext]);

  useEffect(() => {
    if (isEditing) {
      setErrors({});
    }
  }, [isEditing]);

const loadSession = useCallback(async (signal) => { 
    try {
      const data = await ExpertSessionsService.Get(id, authContext, signal); 

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        const d = data.data;
        const startDate = d.scheduledStartDate
          ? new Date(d.scheduledStartDate + "Z")
          : null;
        const endDate = d.scheduledEndDate
          ? new Date(d.scheduledEndDate + "Z")
          : null;

        const toHour12 = (date) => {
          if (!date) return "01";
          let h = date.getHours();
          if (h === 0) h = 12;
          else if (h > 12) h = h - 12;
          return h.toString().padStart(2, "0");
        };
        const toMinute = (date) => {
          if (!date) return "00";
          return date.getMinutes().toString().padStart(2, "0");
        };
        const getMeridian = (date) => {
          if (!date) return "AM";
          return date.getHours() >= 12 ? "PM" : "AM";
        };

        setFormData({
          title: d.title || "",
          courseId: Number(d.courseId),
          instructors: d.instructors || [],
          date: startDate
            ? new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000)
                .toISOString()
                .split("T")[0]
            : "",
          fromHour: toHour12(startDate),
          fromMinute: toMinute(startDate),
          toHour: toHour12(endDate),
          toMinute: toMinute(endDate),
          description: d.description || "",
          meetingLink: d.meetingUrl || "",
        });
        setErrors({});
        setMeetingType(d.meetingPlatform || "Teams");
        setFromMeridian(getMeridian(startDate));
        setToMeridian(getMeridian(endDate));
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    }
  }, [authContext, id]);

useEffect(() => {
    const controller = new AbortController()
    if (formData.courseId) {
      loadInstructorsByCourse(formData.courseId, controller.signal); 
    }
    return () => controller.abort();
  }, [formData.courseId, loadInstructorsByCourse]);

useEffect(() => {
    const controller = new AbortController();
    if (id) {
      setIsEditing(true);
      setSessionId(id);
      loadSession(controller.signal); 
    }
    return () => controller.abort();
  }, [id, loadSession]);

useEffect(() => {
    const controller = new AbortController(); 
    loadCourses(controller.signal);
    return () => controller.abort();
  }, [loadCourses]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
      fromTime: undefined,
      toTime: undefined,
    }));
  };

  const convertTo24Hour = (hour, minute, meridian) => {
    let h = parseInt(hour);
    if (meridian === "PM" && h !== 12) h += 12;
    if (meridian === "AM" && h === 12) h = 0;
    return `${h.toString().padStart(2, "0")}:${minute}:00`;
  };

  const buildISODateTime = (date, hour, minute, meridian) => {
    if (!date) return null;
    const time24 = convertTo24Hour(hour, minute, meridian);
    return new Date(`${date}T${time24}`).toISOString();
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Meeting title is required";
    } else if (formData.title.trim().length < 3) {
      newErrors.title = "Meeting title must be at least 3 characters";
    }

    if (!formData.courseId) {
      newErrors.courseId = "Please select a course";
    }

    if (formData.courseId && formData.instructors.length === 0) {
      newErrors.instructors = "Please select at least one instructor";
    }

    if (!formData.date) {
      newErrors.date = "Date is required";
    } else {
      const selectedDate = new Date(formData.date);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.date = "Cannot select past date";
      } else {
        const startDateTime = new Date(
          `${formData.date}T${convertTo24Hour(
            formData.fromHour,
            formData.fromMinute,
            fromMeridian,
          )}`,
        );

        if (
          selectedDate.toDateString() === today.toDateString() &&
          startDateTime < new Date()
        ) {
          newErrors.fromTime = "Start time cannot be in the past";
        }
      }
    }
    if (!formData.fromHour) {
      newErrors.fromTime = "Start time is required";
    }

    if (!formData.toHour) {
      newErrors.toTime = "End time is required";
    }

    const start24 = convertTo24Hour(
      formData.fromHour,
      formData.fromMinute,
      fromMeridian,
    );
    const end24 = convertTo24Hour(
      formData.toHour,
      formData.toMinute,
      toMeridian,
    );

    if (
      formData.fromHour &&
      formData.toHour &&
      new Date(`1970-01-01T${start24}`) >= new Date(`1970-01-01T${end24}`)
    ) {
      newErrors.toTime = "End time must be after start time";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



const handleSubmit = async (e) => {
  e.preventDefault();

  if (isSubmitting) return;

  if (!validate()) return;

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setIsSubmitting(true);

  try {
    const payload = {
      id: sessionId || 0,
      courseId: formData.courseId,
      courseName:
        courses.find((c) => c.value === formData.courseId)?.label || "",
      title: formData.title,
      description: formData.description,
      meetingPlatform: meetingType,
      meetingUrl: formData.meetingLink || "",
      joinMeetingId: formData.joinMeetingId || "",
      meetingId: formData.meetingId || "",
      passcode: formData.passcode || "",
      instructors: formData.instructors,
      scheduledStartDate: buildISODateTime(
        formData.date,
        formData.fromHour,
        formData.fromMinute,
        fromMeridian,
      ),
      scheduledEndDate: buildISODateTime(
        formData.date,
        formData.toHour,
        formData.toMinute,
        toMeridian,
      ),
      isActive: true,
      isDeleted: false,
    };

    const data = await ExpertSessionsService.Save(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (data.isSuccess) {
      AlertService.success(
        isEditing
          ? "Expert session updated successfully"
          : "Expert session created successfully",
      );
      navigate("/expert-sessions");
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    console.error(err);
  } finally {
    if (!controller.signal.aborted) setIsSubmitting(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

  const onConfirmDeleteHandler = () => setShowDeleteDialog(true);

  const onDeleteHandler = async () => {
    const data = await ExpertSessionsService.Remove(sessionId, authContext);
    if (data.isSuccess) {
      AlertService.success("Session deleted successfully");
      navigate("/expert-sessions");
      sessionStorage.removeItem("expertSessionId");
    } else {
      AlertService.error(data.errorMessage);
    }
  };

  useEffect(() => {
    return () => {
      sessionStorage.removeItem("expertSessionId");
    };
  }, []);

  return (
    <div className="container-fluid pt-4 px-3">
      <form onSubmit={handleSubmit}>
        {/* ROW 1 */}
        <div className="row g-4 mb-4">
          {/* TITLE */}
          <div className="col-md-6">
            <label className="form-label">Meeting Title</label>
            <input
              type="text"
              className="form-control custom-input custom-input-color"
              value={formData.title}
              onChange={(e) => handleChange("title", e.target.value)}
            />
            {errors.title && (
              <small className="text-danger">{errors.title}</small>
            )}
          </div>

          {/* COURSE*/}
          <div className="col-md-6">
            <label className="form-label">Course</label>
            <select
              className="form-select custom-input-color custom-input"
              value={formData.courseId}
              onChange={(e) => {
                const courseId = Number(e.target.value);

                handleChange("courseId", courseId);

                // clear old instructors
                handleChange("instructors", []);
              }}
            >
              <option value="">Select Course</option>
              {courses.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            {errors.courseId && (
              <small className="text-danger">{errors.courseId}</small>
            )}
          </div>

          {/* INSTRUCTOR */}
          <div className="col-md-6">
            <label className="form-label">Instructor</label>
            <MultiSelect
              value={formData.instructors}
              options={users}
              onChange={(e) => handleChange("instructors", e.value)}
              optionLabel="label"
              optionValue="value"
              placeholder={
                formData.courseId ? "Select Instructor" : "Select course first"
              }
              className={`w-100 custom-input-color custom-input ${
                !formData.courseId ? "opacity-50" : ""
              }`}
              display="chip"
              filter
              loading={loadingUsers}
              disabled={!formData.courseId}
            />
            {formData.courseId && errors.instructors && (
              <small className="text-danger">{errors.instructors}</small>
            )}
          </div>




          {/* DATE */}
          <div className="col-md-6">
            <label className="form-label">Date</label>
          <input
  type="date"
  min={new Date().toISOString().split("T")[0]}
  className="form-control custom-input custom-input-color"
  value={formData.date}
  onChange={(e) => handleChange("date", e.target.value)}
  onKeyDown={(e) => e.preventDefault()}
  onPaste={(e) => e.preventDefault()}
  onFocus={(e) => {
    if (e.target.showPicker) {
      e.target.showPicker(); 
    }
  }}
/>
            {errors.date && (
              <small className="text-danger">{errors.date}</small>
            )}
          </div>
        </div>

        {/* TIME + DESCRIPTION */}
        <div className="row g-4 mb-4">
          <div className="col-md-6">
            {/* START TIME */}
            <label className="form-label">Start Time</label>
            <div className="mb-2">
              <div className="d-flex gap-2">
                <select
                  className="form-select custom-input-color custom-input"
                  value={formData.fromHour}
                  onChange={(e) => handleChange("fromHour", e.target.value)}
                >
                  {hours.map((h) => (
                    <option key={h}>{h}</option>
                  ))}
                </select>
                <span className="align-self-center">:</span>
                <select
                  className="form-select custom-input-color custom-input"
                  value={formData.fromMinute}
                  onChange={(e) => handleChange("fromMinute", e.target.value)}
                >
                  {minutes.map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
                <select
                  className="form-select custom-input-color custom-input"
                  style={{ maxWidth: "90px" }}
                  value={fromMeridian}
                  onChange={(e) => {
                    setFromMeridian(e.target.value);
                    setErrors((prev) => ({
                      ...prev,
                      fromTime: undefined,
                      toTime: undefined,
                    }));
                  }}
                >
                  <option>AM</option>
                  <option>PM</option>
                </select>
              </div>
              {errors.fromTime && (
                <small className="text-danger mt-0">{errors.fromTime}</small>
              )}
            </div>

            {/* END TIME */}
            <label className="form-label">End Time</label>
            <div className="d-flex gap-2">
              <select
                className="form-select custom-input-color custom-input"
                value={formData.toHour}
                onChange={(e) => handleChange("toHour", e.target.value)}
              >
                {hours.map((h) => (
                  <option key={h}>{h}</option>
                ))}
              </select>
              <span className="align-self-center">:</span>
              <select
                className="form-select custom-input-color"
                value={formData.toMinute}
                onChange={(e) => handleChange("toMinute", e.target.value)}
              >
                {minutes.map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
              <select
                className="form-select custom-input-color"
                style={{ maxWidth: "90px" }}
                value={toMeridian}
                onChange={(e) => {
                  setToMeridian(e.target.value);
                  setErrors((prev) => ({
                    ...prev,
                    fromTime: undefined,
                    toTime: undefined,
                  }));
                }}
              >
                <option>AM</option>
                <option>PM</option>
              </select>
            </div>
            {errors.toTime && (
              <small className="text-danger">{errors.toTime}</small>
            )}
          </div>

          {/* DESCRIPTION */}
          <div className="col-md-6">
            <label className="form-label">Description</label>
            <textarea
              className="form-control custom-input-color"
              rows="6"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
            />
          </div>
        </div>

        {/* MEETING TYPE */}
        <div className="row mb-4">
          <div className="col-md-12 d-flex gap-4">
            <label className="fw-medium">Choose Meeting Type :</label>
<div className="form-check">
  <input
    type="radio"
    className="form-check-input"
    checked={meetingType === "Teams"}
    onChange={() => setMeetingType("Teams")}
    disabled={isEditing}
  />
  <label>Teams</label>
</div>

<div className="form-check">
  <input
    type="radio"
    className="form-check-input"
    checked={meetingType === "Zoom"}
    onChange={() => setMeetingType("Zoom")}
    disabled={isEditing}
  />
  <label>Zoom</label>
</div>
          </div>
        </div>

        {/* BUTTONS */}
        <div className="d-flex justify-content-between mt-5">
          {isEditing && permissions?.showDelete && (
            <button
              type="button"
              className="btn btn-outline-danger px-4"
              onClick={onConfirmDeleteHandler}
            >
              <FaTrash className="me-2" />
              Delete
            </button>
          )}
          <div className="ms-auto d-flex gap-3">
            <button
              type="button"
              className="btn btn-outline-secondary px-5 py-2"
              onClick={() => navigate("/expert-sessions")}
            >
              Cancel
            </button>
            {(permissions.showAdd || permissions.showEdit) && (
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn text-white btn-primary px-5 py-2"
              >
                {isSubmitting
                  ? "Submitting..."
                  : isEditing
                    ? "Update"
                    : "Submit"}
              </button>
            )}
          </div>
        </div>
      </form>

      <ConfirmDialogBox
        visible={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
        onConfirm={() => {
          setShowDeleteDialog(false);
          onDeleteHandler();
        }}
        title={
          <>
            Are you sure you want to <strong>delete</strong> this session?
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default CreateExpertSession;
